import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Star, Image as ImageIcon, Video } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useState, useMemo } from 'react';
import type { MediaItem } from '../hooks/useMediaGallery';
import { getFileUrl } from '@/shared/hooks/useFileUpload';

type MediaItemCardProps = {
  item: MediaItem;
  onRemove: (itemId: string) => void;
  onSetPrimary: (itemId: string) => void;
  onCaptionChange: (itemId: string, caption: string) => void;
};

/**
 * MediaItemCard - Draggable card for media gallery items
 * 
 * Features:
 * - Drag handle for reordering
 * - Image/video preview
 * - Caption editing (inline input)
 * - Primary selection (star icon)
 * - Remove button with confirmation
 * - Visual feedback during drag
 * 
 * Integration:
 * - Uses @dnd-kit/sortable for drag-drop
 * - Managed by MediaGalleryModal
 * - State managed via useMediaGallery hook
 * 
 * Technical:
 * - Supports both image and video media types
 * - Primary item indicated with gold star
 * - Dragging shows opacity effect
 */
export function MediaItemCard({
  item,
  onRemove,
  onSetPrimary,
  onCaptionChange,
}: MediaItemCardProps) {
  const [captionValue, setCaptionValue] = useState(item.caption || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Hydrate S3 file key to full URL (for backend data)
  const displayUrl = useMemo(() => {
    const url = item.mediaUrl;
    
    if (!url) return '';
    
    // If already a full URL or blob URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    
    // Convert S3 file key to full URL
    return getFileUrl(url);
  }, [item.mediaUrl]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleCaptionBlur = () => {
    if (captionValue !== (item.caption || '')) {
      onCaptionChange(item.id, captionValue);
    }
  };

  const handleRemove = () => {
    if (showDeleteConfirm) {
      onRemove(item.id);
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
      // Auto-cancel confirmation after 3 seconds
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  const handleSetPrimary = () => {
    onSetPrimary(item.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors"
    >
      <div className="flex gap-3">
        {/* Drag Handle */}
        <button
          type="button"
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5" />
        </button>

        {/* Preview */}
        <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
          {item.mediaType === 'image' ? (
            <img
              src={displayUrl}
              alt={item.caption || 'Media preview'}
              className="w-full h-full object-cover"
            />
          ) : (
            <Video className="h-8 w-8 text-gray-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Caption Input */}
          <Input
            value={captionValue}
            onChange={(e) => setCaptionValue(e.target.value)}
            onBlur={handleCaptionBlur}
            placeholder="Add caption..."
            className="text-sm"
          />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Primary Toggle */}
            <Button
              type="button"
              variant={item.isPrimary ? 'default' : 'outline'}
              size="sm"
              onClick={handleSetPrimary}
              className={item.isPrimary ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
            >
              <Star className={`h-3 w-3 mr-1 ${item.isPrimary ? 'fill-white' : ''}`} />
              {item.isPrimary ? 'Primary' : 'Set Primary'}
            </Button>

            {/* Remove Button */}
            <Button
              type="button"
              variant={showDeleteConfirm ? 'destructive' : 'outline'}
              size="sm"
              onClick={handleRemove}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              {showDeleteConfirm ? 'Confirm?' : 'Remove'}
            </Button>
          </div>

          {/* Media Type & Order Badge */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {item.mediaType === 'image' ? (
              <span className="inline-flex items-center">
                <ImageIcon className="h-3 w-3 mr-1" />
                Image
              </span>
            ) : (
              <span className="inline-flex items-center">
                <Video className="h-3 w-3 mr-1" />
                Video
              </span>
            )}
            <span>•</span>
            <span>Position: {item.displayOrder + 1}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

